// ═══════════════════════════════════════════════════════════════════════════
// grainSaleReason.js — classifies WHY a sale would be attractive right now
// into named categories (Kevin, 2026-08-18 "GRAIN MARKET INTELLIGENCE UI"
// item 5: "Separate reasons for sale into at least: OPPORTUNITY SALE,
// RELATIVE-OPPORTUNITY SALE, DISCIPLINE SALE... Do not invent forced-sale
// percentages or calendar checkpoints without a defensible methodology.
// Build the seam and explicit state first; unknown beats fabricated
// precision.").
//
// Every input here is a field this codebase ALREADY computes elsewhere —
// this file composes, it does not add a second scoring engine:
//   - OPPORTUNITY  <- marketOpportunityState.category (grainAction.js),
//                      itself opportunityCategory(tc.pctile) — the SAME
//                      67th-percentile cut the Futures gauge already uses.
//   - RELATIVE     <- action.cash.basisContext.context (grainCash.js's
//                      basisHistoryContext) — "is the OBSERVED basis
//                      historically strong vs. its own comparable history,"
//                      a genuinely RELATIVE read that can fire even when the
//                      absolute futures level is unremarkable.
//   - DISCIPLINE   <- grainMarketingClock.js's resolveMarketingClock(),
//                      which is DELIBERATELY always methodologyRequired:true
//                      today (Kevin, 2026-08-18 21:45: "Do not invent
//                      calendar checkpoints, forced-sale percentages...").
//                      This file NEVER invents a discipline trigger — it
//                      reports discipline as an explicit "not yet built"
//                      state, exactly mirroring the clock's own honesty.
//
// A farm can have MORE THAN ONE reason true at once (e.g. futures strong AND
// basis strong) — `active` lists every category that currently fires,
// `primary` is the single best-known one for a compact chip, chosen by a
// fixed, stated priority (OPPORTUNITY > RELATIVE > DISCIPLINE), never a
// weighted score.
//
// PURE — no fetch, no React, no Supabase.
// ═══════════════════════════════════════════════════════════════════════════

export const SALE_REASON = {
  OPPORTUNITY: "OPPORTUNITY SALE",
  RELATIVE: "RELATIVE-OPPORTUNITY SALE",
  DISCIPLINE: "DISCIPLINE SALE",
  NONE: "NO CURRENT SALE REASON",
  UNKNOWN: "UNKNOWN",
};

// componentGuidance — Kevin, 2026-08-19 overnight watchdog item 3:
// "Component action must follow the two gauges. Strong futures + ordinary/
// unknown basis => guidance can be 'protect futures / leave basis open,'
// never force a cash sale or destination. Strong basis + ordinary futures
// must remain distinguishable. Strong both can support cash execution only
// if logistics/destination are verified. Unknown logistics stays
// non-executable/example-only." Composes ONLY the two `fires` flags this
// file already derives plus a logistics flag the caller already resolved
// (grainCash.js's logisticsConfidence === "confirmed") — no new scoring, no
// new threshold, just naming which of the four already-real states applies.
function componentGuidance(opportunityFires, relativeFires, logisticsConfirmed) {
  if (opportunityFires && relativeFires) {
    return logisticsConfirmed
      ? "both futures and basis read attractive, and a delivery destination is confirmed for this farm — cash/forward-cash execution is a legitimate candidate."
      : "both futures and basis read attractive, but no confirmed delivery destination exists for this farm — treat any cash figure shown as an observed example, not an executable price; futures price protection remains available regardless of logistics.";
  }
  if (opportunityFires) {
    return "futures reads the stronger signal here — consider adding FUTURES price protection (e.g. an HTA); basis does not need to be fixed at the same time.";
  }
  if (relativeFires) {
    return "basis reads the stronger signal here, not the futures level — a basis-only pricing vehicle may be more relevant than a full cash sale or a futures-only hedge.";
  }
  return "neither futures nor basis reads as a current opportunity — no component-specific guidance today.";
}

// classifySaleReason({ marketOpportunityState, basisContext, marketingClock })
//   marketOpportunityState: grainAction.js's own field, or a plain
//     { category } shape ({ category: 'strong'|'normal'|'weak'|'unknown' }).
//   basisContext: cash.basisContext (grainCash.js's basisHistoryContext()
//     output), or null/undefined when no cash source resolved.
//   marketingClock: resolveMarketingClock() output (grainMarketingClock.js),
//     or null/undefined when the caller hasn't wired it — DISCIPLINE always
//     reads not-yet-built either way, so this argument is optional.
//   logisticsConfirmed: cash.logisticsConfidence === "confirmed" (grainCash.js),
//     or false/undefined — drives ONLY the wording of componentGuidance when
//     both futures and basis fire; never changes `active`/`primary`.
export function classifySaleReason({ marketOpportunityState = null, basisContext = null, marketingClock = null, logisticsConfirmed = false } = {}) {
  const active = [];

  const oppCategory = marketOpportunityState ? marketOpportunityState.category : null;
  const opportunity = {
    fires: oppCategory === "strong",
    known: !!oppCategory && oppCategory !== "unknown",
    category: oppCategory || "unknown",
    detail: oppCategory === "strong"
      ? "futures percentile reads STRONG for this contract"
      : (oppCategory && oppCategory !== "unknown" ? `futures percentile reads ${oppCategory.toUpperCase()}, not strong enough alone` : "futures percentile unknown"),
  };
  if (opportunity.fires) active.push(SALE_REASON.OPPORTUNITY);

  const bcAvailable = !!(basisContext && basisContext.available);
  const bcCategory = bcAvailable ? basisContext.context : null;
  const relative = {
    fires: bcAvailable && bcCategory === "strong",
    known: bcAvailable,
    category: bcCategory || "unknown",
    detail: bcAvailable
      ? (bcCategory === "strong"
          ? "observed basis reads STRONG vs. its own comparable history, even if futures alone is not"
          : `observed basis reads ${String(bcCategory || "unknown").toUpperCase()} vs. its own comparable history`)
      : "no comparable basis history resolved for this candidate",
  };
  if (relative.fires) active.push(SALE_REASON.RELATIVE);

  const clockRequiresMethodology = !marketingClock || marketingClock.methodologyRequired !== false;
  const discipline = {
    fires: false, // never fires — no sourced/calibrated methodology exists yet (Kevin, 2026-08-18)
    known: false,
    methodologyRequired: clockRequiresMethodology,
    detail: "no sourced/backtested marketing-clock methodology exists yet to trigger a discipline sale — this is an explicit gap, not a fabricated trigger",
  };
  // discipline intentionally never enters `active` — see header.

  let primary = SALE_REASON.NONE;
  if (active.includes(SALE_REASON.OPPORTUNITY)) primary = SALE_REASON.OPPORTUNITY;
  else if (active.includes(SALE_REASON.RELATIVE)) primary = SALE_REASON.RELATIVE;
  else if (!opportunity.known && !relative.known) primary = SALE_REASON.UNKNOWN;

  return {
    primary, active, opportunity, relative, discipline,
    componentGuidance: componentGuidance(opportunity.fires, relative.fires, !!logisticsConfirmed),
  };
}
